export interface NavItemDef {
  to: string;
  label: string;
  end?: boolean;
  /** If set, user must have at least one of these permissions to see the item. */
  anyOf?: readonly string[];
  /** Sidebar section heading (admin menu). */
  group?: string;
}

/** Dispatch / booking UI — active dispatch perms only (not trips.view_all alone). */
export const DISPATCH_UI_PERMISSIONS = [
  'trips.book',
  'trips.confirm',
  'trips.dispatch',
  'trips.correct',
] as const;

/** Attendance log (clock in/out with location) — must match the API's
 * GET /execution/attendance/log check. */
export const ATTENDANCE_LOG_PERMISSIONS = ['trips.dispatch', 'trips.view_all'] as const;

export const internalNavItems: NavItemDef[] = [
  {
    to: '/app/dispatch',
    label: 'Dispatch board',
    end: true,
    anyOf: DISPATCH_UI_PERMISSIONS,
  },
  {
    to: '/app/dispatch/today',
    label: 'Today',
    anyOf: DISPATCH_UI_PERMISSIONS,
  },
  {
    to: '/app/dispatch/live',
    label: 'Live map',
    anyOf: DISPATCH_UI_PERMISSIONS,
  },
  {
    to: '/app/dispatch/attendance',
    label: 'Attendance',
    anyOf: ATTENDANCE_LOG_PERMISSIONS,
  },
  {
    to: '/app/dispatch/approvals',
    label: 'Approvals',
    anyOf: ['trips.confirm'],
  },
  {
    to: '/app/dispatch/book',
    label: 'New booking',
    anyOf: ['trips.book'],
  },
  {
    to: '/app/admin',
    label: 'Admin',
    end: true,
    anyOf: [
      'settings.manage',
      'billing.run',
      'pay.run',
      'margin.view',
      'fleet.manage',
      'clients.manage',
      'rates.manage',
      'pay_cards.manage',
      'users.manage',
      'integrations.manage',
    ],
  },
  {
    to: '/app/sales',
    label: 'Sales',
    end: true,
    anyOf: ['sales.view_own', 'sales.submit_account', 'sales.manage_plans'],
  },
  { to: '/notifications', label: 'Notifications' },
];

export const adminNavItems: NavItemDef[] = [
  { to: '/app/admin', label: 'Setup checklist', end: true, anyOf: ['settings.manage'], group: 'Setup' },
  { to: '/app/admin/settings', label: 'Settings', anyOf: ['settings.manage'], group: 'Setup' },
  { to: '/app/admin/zones', label: 'Zones', anyOf: ['settings.manage'], group: 'Setup' },
  { to: '/app/admin/vehicles', label: 'Vehicles', anyOf: ['fleet.manage'], group: 'Setup' },
  {
    to: '/app/admin/people',
    label: 'People',
    anyOf: ['users.manage', 'roles.manage', 'fleet.manage', 'trips.dispatch', 'trips.book'],
    group: 'Setup',
  },
  { to: '/app/admin/clients', label: 'Clients', anyOf: ['clients.manage'], group: 'Setup' },
  {
    to: '/app/admin/addresses',
    label: 'Address book',
    anyOf: ['clients.manage', 'settings.manage', 'trips.book'],
    group: 'Setup',
  },
  { to: '/app/admin/partners', label: 'Subcontractors', anyOf: ['fleet.manage'], group: 'Setup' },
  { to: '/app/admin/integrations', label: 'Integrations', anyOf: ['integrations.manage', 'settings.manage'], group: 'Setup' },
  { to: '/app/admin/crew-app', label: 'Crew app', anyOf: ['settings.manage'], group: 'Setup' },
  {
    to: '/app/admin/rate-cards',
    label: 'Rate cards',
    anyOf: ['rates.manage', 'rates.activate'],
    group: 'Pricing & pay',
  },
  { to: '/app/admin/pay-cards', label: 'Pay cards', anyOf: ['pay_cards.manage'], group: 'Pricing & pay' },
  { to: '/app/admin/fuel', label: 'Fuel prices', anyOf: ['settings.manage'], group: 'Pricing & pay' },
  { to: '/app/admin/billing', label: 'Billing', anyOf: ['billing.run', 'billing.push_xero'], group: 'Finance' },
  { to: '/app/admin/costs', label: 'Cost allocation', anyOf: ['margin.view'], group: 'Finance' },
  { to: '/app/admin/payouts', label: 'Payouts', anyOf: ['pay.run'], group: 'Finance' },
  { to: '/app/admin/commission-plans', label: 'Commission plans', anyOf: ['sales.manage_plans'], group: 'Finance' },
  { to: '/app/admin/commission-settlement', label: 'Commission settlement', anyOf: ['commission.settle'], group: 'Finance' },
  { to: '/app/admin/audit', label: 'Audit log', anyOf: ['audit.view'], group: 'Finance' },
  {
    to: '/app/dispatch',
    label: '← Dispatch board',
    anyOf: DISPATCH_UI_PERMISSIONS,
    group: 'Dispatch',
  },
];

export const crewNavItems: NavItemDef[] = [
  { to: '/crew', label: 'Trip', end: true },
  { to: '/crew/scan', label: 'Scan' },
  { to: '/crew/pod', label: 'Sign' },
  { to: '/crew/costs', label: 'Costs' },
  { to: '/crew/earnings', label: 'Pay' },
  { to: '/notifications', label: 'Alerts' },
];

export const portalNavItems: NavItemDef[] = [
  { to: '/portal', label: 'Trips', end: true, anyOf: ['portal.view_trips'] },
  { to: '/portal/documents', label: 'Documents', anyOf: ['portal.download_docs'] },
  { to: '/portal/book', label: 'Book', anyOf: ['portal.book'] },
  { to: '/portal/users', label: 'Users', anyOf: ['portal.manage_users'] },
  { to: '/notifications', label: 'Notifications' },
];

export const salesNavItems: NavItemDef[] = [
  { to: '/app/sales', label: 'Dashboard', end: true },
  { to: '/app/sales/prospects', label: 'Prospects' },
  { to: '/app/sales/submit', label: 'Submit account' },
  { to: '/app/sales/settlements', label: 'Settlements' },
  { to: '/notifications', label: 'Notifications' },
];

export function filterNavByPermission(
  items: NavItemDef[],
  hasPermission: (permission: string) => boolean,
): NavItemDef[] {
  return items.filter((item) => !item.anyOf?.length || item.anyOf.some((p) => hasPermission(p)));
}

/** First admin destination for the signed-in user (Finance → billing, not setup checklist). */
export function defaultAdminPath(hasPermission: (permission: string) => boolean): string {
  if (hasPermission('settings.manage')) return '/app/admin';
  if (hasPermission('billing.run') || hasPermission('billing.push_xero')) return '/app/admin/billing';
  if (hasPermission('pay.run')) return '/app/admin/payouts';
  if (hasPermission('margin.view')) return '/app/admin/costs';
  if (hasPermission('fleet.manage')) return '/app/admin/vehicles';
  if (hasPermission('clients.manage')) return '/app/admin/clients';
  return '/app/admin';
}
